import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import './registrationcss/Register.css';
import './registrationcss/GuardianContactDetails.css';
import RegistrationProgressBar from '../../components/usercomponents/RegistrationProgressBar';
import guardianContactImg from '../../images/PersonalInfo_IMG.png';
import logo from '../../images/auravit_logo.png';

const relationshipOptions = [
  { value: 'Parent', label: 'Parent' },
  { value: 'Partner', label: 'Partner' },
  { value: 'Guardian', label: 'Guardian' },
  { value: 'Sibling', label: 'Sibling' },
  { value: 'Relative', label: 'Relative' },
  { value: 'others', label: 'Others' }
];

export default function GuardianContactDetails() {
  const location = useLocation();
  const navigate = useNavigate();
  
  // State management
  const [formData, setFormData] = useState({
    guardianFullName: '',
    relationshipToMinor: '',
    relationshipOther: '',
    guardianContactNumber: '',
    guardianAddress: '',
    consent: false
  });
  
  const [errors, setErrors] = useState({
    guardianFullName: '',
    relationshipToMinor: '',
    guardianContactNumber: '',
    guardianAddress: '',
    consent: ''
  });
  const [isSaving, setIsSaving] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);

  // Hydrate from saved draft when navigating back
  useEffect(() => {
    const draft = JSON.parse(localStorage.getItem('registrationDraft') || '{}');
    if (!draft.guardianFullName && !draft.guardianContactNumber && !draft.guardianAddress) return;

    // guardianContactNumber is stored as +63XXXXXXXXXX — strip prefix for the input
    const rawContact = (draft.guardianContactNumber || '').replace(/^\+63/, '');

    // guardianRelationshipToUser may be a custom value (others) or a preset option
    const presetValues = relationshipOptions.map(o => o.value);
    const isPreset = presetValues.includes(draft.guardianRelationshipToUser);
    const relationshipToMinor = isPreset ? draft.guardianRelationshipToUser : (draft.guardianRelationshipToUser ? 'others' : '');
    const relationshipOther = !isPreset ? (draft.guardianRelationshipToUser || '') : '';

    setFormData({
      guardianFullName: draft.guardianFullName || '',
      relationshipToMinor,
      relationshipOther,
      guardianContactNumber: rawContact,
      guardianAddress: draft.guardianAddress || '',
      consent: false, // consent must always be re-confirmed
    });
  }, []);

  // Get user data from location state
  const userEmail = location.state?.email || '';
  const userContact = location.state?.contact || '';

  // Validation functions
  const validateName = (value) => {
    if (!value.trim()) return "Full name is required";
    if (!/^[A-Za-z\s'-]*$/.test(value)) return "Invalid name format";
    if (value.trim().length > 50) return "Maximum 50 characters";
    return "";
  };


  const validateContact = (value) => {
    const digits = value.replace(/[^\d]/g, '');
    if (!value) return "Contact number is required";
    if (digits.length !== 10) return "Contact number must be 10 digits (e.g., 9123456789)";
    if (!digits.startsWith('9')) return "Contact number must start with 9";
    if (digits === userContact.replace(/[^\d]/g, '')) return "Contact number cannot be the same as user contact number";
    return "";
  };

  const validateAddress = (value) => {
    if (!value.trim()) return "Address is required";
    if (value.trim().length < 5) return "Address must be at least 5 characters";
    if (value.trim().length > 100) return "Address must be less than 100 characters";
    if (!/^[A-Za-z0-9\s'-]*$/.test(value)) return "Address can only contain letters, numbers, spaces, and hyphens";
    return "";
  };

  const validateRelationship = (value) => {
    if (!value) return "Relationship is required";
    if (value === 'others' && !formData.relationshipOther.trim()) return "Please specify relationship";
    return "";
  };

  // Handle input changes
  const handleChange = (field, value) => {
    let processedValue = value;
    
    if (field === 'guardianFullName') {
      processedValue = value.replace(/[^A-Za-z\s'-]/g, '').slice(0, 50);
      setErrors(prev => ({ ...prev, guardianFullName: validateName(processedValue) }));
    } else if (field === 'guardianAddress') {
      processedValue = value.replace(/[^A-Za-z0-9\s'-]/g, '').slice(0, 100);
      setErrors(prev => ({ ...prev, guardianAddress: validateAddress(processedValue) }));
    } else if (field === 'guardianContactNumber') {
      processedValue = value.replace(/[^\d]/g, '').slice(0, 10);
      setErrors(prev => ({ ...prev, guardianContactNumber: validateContact(processedValue) }));
    } else if (field === 'relationshipOther') {
      processedValue = value.replace(/[^A-Za-z\s'-]/g, '').slice(0, 20);
      setErrors(prev => ({ ...prev, relationshipToMinor: validateRelationship(formData.relationshipToMinor) }));
    }

    setFormData(prev => ({ ...prev, [field]: processedValue }));
  };

  // Handle dropdown selection
  const handleDropdownSelect = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setOpenDropdown(null);
    setErrors(prev => ({ ...prev, [field]: '' }));
  };

  // Form validation
  const validateForm = () => {
    const newErrors = {
      guardianFullName: validateName(formData.guardianFullName),
      relationshipToMinor: validateRelationship(formData.relationshipToMinor),
      guardianContactNumber: validateContact(formData.guardianContactNumber),
      guardianAddress: validateAddress(formData.guardianAddress),
      consent: !formData.consent ? 'Consent is required' : ''
    };
    setErrors(newErrors);
    return !Object.values(newErrors).some(error => error);
  };

  // Handle form submission
  const handleContinue = async () => {
    setHasSubmitted(true);
    if (!validateForm()) return;
    if (isSaving) return;

    setIsSaving(true);
    try {
      // Store in localStorage for registration flow
      const storedDraft = JSON.parse(localStorage.getItem('registrationDraft') || '{}');
      const updatedDraft = {
        ...storedDraft,
        ...location.state,
        guardianFullName: formData.guardianFullName.trim(),
        guardianRelationshipToUser: formData.relationshipToMinor === 'others' ? formData.relationshipOther.trim() : formData.relationshipToMinor,
        guardianContactNumber: formData.guardianContactNumber ? `+63${formData.guardianContactNumber}` : '',
        guardianAddress: formData.guardianAddress.trim(),
        // Clear accompanist details when saving guardian details
        accompanistFullName: '',
        accompanistRelationshipToUser: '',
        accompanistContactNumber: '',
        registrationStep: 5,
      };

      localStorage.setItem('registrationDraft', JSON.stringify(updatedDraft));

      // Navigate to next step
      navigate('/account-setup', {
        state: { ...updatedDraft, isMinor: true }
      });
    } catch (err) {
      console.error('Guardian contact save error:', err);
      alert('Failed to save guardian information. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };


  return (
    <>
      <div className="login-logo-container">
        <img 
          src={logo} 
          alt="AuraVit" 
          className="login-logo"
          onClick={() => navigate("/")}
        />
      </div>
      <div className="register-container">
        <div className="register-card">
          <div className="left-panel">
            <img
              src={guardianContactImg}
              alt="Guardian Contact"
              className="panel-image"
            />
          </div>
          <div className="right-panel">
            <div className="registration-header-left">
              <h1 className="create-account-title">Create Account</h1>
              <p className="wellness-subtitle">
                For minors, we need parent/guardian contact information and consent.
              </p>
            </div>

            <RegistrationProgressBar currentStep={4} />

            {/* Display submit error if backend save fails */}
            {errors.submit && (
              <div className="alert alert-error" role="alert">
                {errors.submit}
              </div>
            )}

            <form noValidate>

              {/* Guardian Full Name */}
            <div className="form-row">
              <div className="input-group">
                <label htmlFor="guardianFullName">
                  Guardian Full Name <span className="required">*</span>
                </label>
                <input
                  id="guardianFullName"
                  type="text"
                  name="guardianFullName"
                  placeholder="Enter guardian's full name"
                  value={formData.guardianFullName}
                  onChange={(e) => handleChange('guardianFullName', e.target.value)}
                  className={errors.guardianFullName ? 'error' : ''}
                  maxLength="50"
                  disabled={isSaving}
                  required
                />
                {errors.guardianFullName && <span className="field-error">{errors.guardianFullName}</span>}
              </div>
            </div>

            {/* Relationship to Minor and Guardian Contact Number */}
            <div className="form-row">
              <div className="input-group">
                <label htmlFor="relationshipToMinor">
                  Relationship to Minor <span className="required">*</span>
                </label>
                <div className="custom-select-wrapper" style={{ width: '100%', position: 'relative' }}>
                  <select
                    id="relationshipToMinor"
                    name="relationshipToMinor"
                    value={formData.relationshipToMinor === 'others' ? 'Other' : formData.relationshipToMinor}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === 'Other') {
                        setFormData(prev => ({ ...prev, relationshipToMinor: 'others', relationshipOther: '' }));
                      } else {
                        setFormData(prev => ({ ...prev, relationshipToMinor: value, relationshipOther: '' }));
                      }
                      // Clear error when selection is made
                      setErrors(prev => ({ ...prev, relationshipToMinor: '' }));
                    }}
                    className={errors.relationshipToMinor ? 'error' : ''}
                    disabled={isSaving}
                    required
                    style={{ 
                      border: '2px solid #e1e5e9',
                      borderRadius: '8px',
                      padding: '12px 16px',
                      fontSize: '1rem',
                      backgroundColor: '#fafafa',
                      width: '100%',
                      appearance: 'none',
                      backgroundImage: 'none',
                      height: '48px',
                      boxSizing: 'border-box',
                      textAlign: 'left'
                    }}
                  >
                    <option value="" disabled hidden>Select Relationship</option>
                    {relationshipOptions.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>

                  {formData.relationshipToMinor === 'others' && (
                    <input
                      type="text"
                      name="relationshipOther"
                      value={formData.relationshipOther}
                      onChange={(e) => handleChange('relationshipOther', e.target.value)}
                      className={errors.relationshipToMinor ? 'error' : ''}
                      placeholder="Specify relationship"
                      maxLength="20"
                      disabled={isSaving}
                      autoFocus
                      style={{ marginTop: '10px' }}
                    />
                  )}
                  <span className="dropdown-icon" />
                </div>
                {errors.relationshipToMinor && <div className="field-error">{errors.relationshipToMinor}</div>}
              </div>

              <div className="input-group">
                <label htmlFor="guardianContactNumber">
                  Guardian Contact Number <span className="required">*</span>
                </label>
                <div className="contact-wrapper">
                  <span className="country-code">+63</span>
                  <input
                    id="guardianContactNumber"
                    type="text"
                    name="guardianContactNumber"
                    placeholder="9123456789"
                    value={formData.guardianContactNumber}
                    onChange={(e) => handleChange('guardianContactNumber', e.target.value)}
                    className={errors.guardianContactNumber ? 'error' : ''}
                    maxLength="10"
                    disabled={isSaving}
                    inputMode="numeric"
                    required
                  />
                </div>
                {errors.guardianContactNumber && <div className="field-error">{errors.guardianContactNumber}</div>}
              </div>
            </div>

            {/* Guardian Address */}
            <div className="form-row">
              <div className="input-group">
                <label htmlFor="guardianAddress">
                  Guardian Address <span className="required">*</span>
                </label>
                <input
                  id="guardianAddress"
                  type="text"
                  name="guardianAddress"
                  placeholder="Enter guardian's address"
                  value={formData.guardianAddress}
                  onChange={(e) => handleChange('guardianAddress', e.target.value)}
                  className={errors.guardianAddress ? 'error' : ''}
                  maxLength="100"
                  disabled={isSaving}
                  required
                />
                {errors.guardianAddress && <div className="field-error">{errors.guardianAddress}</div>}
              </div>
            </div>

            
            {/* Consent Checkbox */}
            <div className="form-row" style={{ marginBottom: '50px' }}>
              <div className="input-group" style={{ textAlign: 'left' }}>
                <label className="consent-checkbox-label" style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  cursor: 'pointer',
                  fontSize: '14px',
                  lineHeight: '1.4',
                  textAlign: 'left',
                  justifyContent: 'flex-start'
                }}>
                  <div style={{ position: 'relative', marginRight: '10px', marginTop: '2px' }}>
                    <input
                      type="checkbox"
                      name="consent"
                      checked={formData.consent}
                      onChange={(e) => {
                        setFormData(prev => ({ ...prev, consent: e.target.checked }));
                        setErrors(prev => ({ ...prev, consent: '' }));
                      }}
                      disabled={isSaving}
                      style={{
                        width: '18px',
                        height: '18px',
                        maxWidth: '18px',
                        maxHeight: '18px',
                        minWidth: '18px',
                        minHeight: '18px',
                        padding: '0',
                        cursor: isSaving ? 'not-allowed' : 'pointer',
                        border: (hasSubmitted && !formData.consent) ? '2px solid #e53935' : '2px solid #003d82',
                        backgroundColor: formData.consent ? '#003d82' : 'transparent',
                        borderRadius: '0px',
                        position: 'relative',
                        appearance: 'none',
                        WebkitAppearance: 'none',
                        MozAppearance: 'none',
                        margin: 0,
                        transition: 'none',
                        boxSizing: 'border-box'
                      }}
                    />
                    {formData.consent && (
                      <svg 
                        style={{ 
                          position: 'absolute', 
                          top: '50%', 
                          left: '50%', 
                          transform: 'translate(-50%, -50%)', 
                          width: '12px', 
                          height: '12px', 
                          color: 'white',
                          pointerEvents: 'none'
                        }} 
                        viewBox="0 0 24 24" 
                        fill="none" 
                        stroke="currentColor" 
                        strokeWidth="3" 
                        strokeLinecap="round" 
                        strokeLinejoin="round"
                      >
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    )}
                  </div>
                  <span style={{
                    position: 'relative',
                    color: '#333',
                    flex: 1,
                    textAlign: 'left'
                  }}>
                    I confirm that I am the parent/guardian and provide consent for this minor to use Auravit services. <span className="required" style={{ color: (hasSubmitted && !formData.consent) ? '#e53935' : 'inherit' }}>*</span>
                  </span>
                </label>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="form-actions">
              <button
                type="button"
                className="continue-btn"
                onClick={handleContinue}
                disabled={isSaving}
              >
                {isSaving ? 'Saving...' : 'Continue'}
              </button>
            </div>
          </form>
          </div>
        </div>
      </div>
    </>
  );
}
